import React from 'react';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';

import Buttons from '../../buttons';
import { CollectionItemHeader, CollectionItemDetail } from '../../typography/Content';
import { getCustomerTitle } from '@repo/utils/Names';

const RowWrapper = styled.div`
  padding: ${(props) => props.theme.margins.xs};
  cursor: ${(props) => (props.onClick ? 'pointer' : 'inherit')};
  display: flex;
  flex-direction: row;
  align-items: center;
  color: ${(props) => props.theme.brandColors.primaryText};
  background-color: ${(props) =>
    props.$inverse
      ? props.theme.brandColors.hsla(props.theme.brandColors.tertiary)
      : props.theme.brandColors.lightestBackground};
  color: ${(props) =>
    props.theme.brandColors[props.$inverse ? 'primaryTextInverse' : 'primaryText']};

  ${(props) =>
    props.$inverse &&
    css`
      & > div > span {
        color: ${(props) => props.theme.brandColors.primaryTextInverse};
      }
    `};

  &:active,
  &:hover {
    background-color: ${(props) => props.theme.brandColors.hsla(props.theme.brandColors.tertiary)};
    & > div > span {
      color: ${(props) => props.theme.brandColors.primaryTextInverse};
    }
  }
`;

const Details = styled.div`
  flex-direction: column;
  flex: 1;
`;

const Name = styled(CollectionItemHeader)`
  display: block;
`;

export function getRecipientName(item, t) {
  // Pre-calculated or provided label when composing from a specific action
  if (item.label) return [item.label, item.allowHide || false];

  const defaultName = item.target?.name || t('commonPlat:labels.unknown');
  let label = defaultName;
  let allowHide = false;
  if (item.target) {
    switch (item.type) {
      case 'employees':
        label = t('commonPlat:labels.employeesOfName', { name: defaultName });
        break;
      case 'volunteers':
        label = t('commonPlat:labels.volunteersOfName', { name: defaultName });
        break;
      case 'members':
        label = t('commonPlat:labels.titleOfName', {
          title: getCustomerTitle(item.target.subType, { grammarCategory: 'plural' }, t),
          name: defaultName,
        });
        break;
      case 'sponsoredadmins':
        label = t('commonPlat:labels.adminsOfEventSupportedByName', {
          name: defaultName,
        });
        break;
      case 'opportunities':
      case 'events':
        label =
          item.type === 'events'
            ? t('commonPlat:labels.activeVolunteersOfNameEvent', {
                name: defaultName,
              })
            : t('commonPlat:labels.activeVolunteersOfNamePosition', {
                name: defaultName,
              });
        allowHide = true;
        break;
      case 'attendees':
        label = t('commonPlat:labels.attendeesOfName', { name: defaultName });
        allowHide = true;
        break;
      case 'formsubs':
        label = t('commonPlat:labels.allUsersFilledOutName', {
          name: defaultName,
        });
        allowHide = true;
        break;
      case 'formsubsappr':
        label = t('commonPlat:labels.approvedUsersFilledOutName', {
          name: defaultName,
        });
        allowHide = true;
        break;
      case 'formsubsrej':
        label = t('commonPlat:labels.declinedUsersFilledOutName', {
          name: defaultName,
        });
        allowHide = true;
        break;
      case 'administrators':
        label = t('commonPlat:labels.adminsOfName', { name: defaultName });
        break;
      case 'limitedadmins':
        label = t('commonPlat:labels.limitedAdminsOfName', {
          name: defaultName,
        });
        break;
      case 'permrole':
        // Do nothing, the server provides the label without need for translation
        break;
      case 'ownergroup':
        label = `${defaultName} ${`(${t('sMgmt:labels.ownerGroup')})`}`;
        break;
      case 'users':
        label = t('commonPlat:labels.nameIndividual', {
          name: `${item.target.firstName || item.target.first_name} ${
            item.target.lastName || item.target.last_name
          }`,
        });
        break;
    }
  }
  return [label, allowHide];
}

function RecipientRow({ containerRef, inverse, onAction, onClick, onKeyPress, ...item }) {
  const { t } = useTranslation(['commonPlat']);
  const [name, allowHide] = getRecipientName(item, t);
  return (
    <RowWrapper ref={containerRef} $inverse={inverse} onClick={onClick} onKeyPress={onKeyPress}>
      <Details>
        <Name inverse={inverse}>{name}</Name>
        <CollectionItemDetail>
          {t('commonPlat:labels.numRecipients', {
            count: parseInt(item.numUsers, 10),
          })}
        </CollectionItemDetail>
      </Details>
      {allowHide && onAction && (
        <Buttons.Action
          icon="eye-slash"
          onClick={
            onAction
              ? (e) => {
                  // Keep dropdown open
                  e.preventDefault();
                  onAction('hide');
                }
              : undefined
          }
        />
      )}
    </RowWrapper>
  );
}

export { RecipientRow };
